import itiriri from 'itiriri';
import { WardDto, HSFacilityGroup } from 'server-openapi';

interface FacilityUtilStore {
  facilityStore: ReadonlyMap<string, WardDto>;
}

//
// There can be multiple facilities (i.e. wards) that share the same MS Ward Name but have to use different HS Facility Names
// so can be supplied by different pharmacies, even though they are essentially the one physical ward. We only want to display
// one checkbox item per MS Ward Name in this SelectWard checkbox list so just take the first hsId for the value then the
// contributingFacilityIds can be used to set all the selected hsId's to control visibility of patients on dashboard when
// the wards checkboxes selections are changed.
//
export type CombinedHSFacility = WardDto & {
  contributingHsIds: number[];
}
export class FacilityUtils {
  private stores: FacilityUtilStore;

  constructor(apiStores: FacilityUtilStore) {
    this.stores = apiStores;
  }

  public fetchAllFacilities = (facilityGroup?: HSFacilityGroup) =>
    fetchAllFacilities(this.stores.facilityStore, facilityGroup);

  public fetchAllFacilitiesByUniqueMsName = (facilityGroup?: HSFacilityGroup) =>
      fetchAllFacilitiesByUniqueMsName(this.stores.facilityStore, facilityGroup);

  public fetchFacilityById = (facilityId?: number) => fetchFacilityById(this.stores.facilityStore, facilityId);
}

function fetchAllFacilities(facilityStore: ReadonlyMap<string, WardDto>, facilityGroup?: HSFacilityGroup) {
  return itiriri(facilityStore.values())
    .filter((f) => f.facilityGroupId === facilityGroup?.hsId)
    .toArray();
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function fetchAllFacilitiesByUniqueMsName(facilityStore: ReadonlyMap<string, WardDto>, facilityGroup?: HSFacilityGroup) {

    // Get the filtered facilities matching the group
    const filteredFacilities = fetchAllFacilities(facilityStore, facilityGroup);

    // Map to store combined results by msWardNameOrName
    const combinedFacilitiesMap: Map<string, CombinedHSFacility> = new Map();

    for (const facility of filteredFacilities) {
        const { msWardNameOrName, hsId } = facility;

        // Skip if msWardNameOrName is not defined
        if(!msWardNameOrName){
            continue;
        }

        if (combinedFacilitiesMap.has(msWardNameOrName)) {
            // Then need to add the hsId to the existing facility's contributingHsIds array so it can track all the
            // ones that share the MS Ward Name.
            const existingFacility = combinedFacilitiesMap.get(msWardNameOrName)!;
            addToContributingHsIds(existingFacility, hsId);
        } else {
            // Create a new CombinedHSFacility and add it to the map
            combinedFacilitiesMap.set(msWardNameOrName, {
                ...facility,
                contributingHsIds: hsId !== undefined ? [hsId] : []
            });
        }
    }

    return Array.from(combinedFacilitiesMap.values());
}

// Helper function to add an hsId to the contributingHsIds array
function addToContributingHsIds(facility: CombinedHSFacility, hsId?: number): void {
    if (hsId !== undefined) {
        facility.contributingHsIds.push(hsId);
    }
}

function fetchFacilityById(facilityStore: ReadonlyMap<string, WardDto>, facilityId?: number) {
  return itiriri(facilityStore.values()).find((facility) => facility.hsId === facilityId);
}
